import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于欧美精品',
    description: '欧美精品品牌介绍 - 汇聚全球顶尖品牌，为您精选来自欧洲与美国的高端正品好物。',
    url: 'https://www.oumeijingpin.com/about',
  };

  const milestones = [
    { year: '2018', title: '品牌创立', desc: '欧美精品于上海正式成立，立志成为连接中国消费者与欧美高端品牌的桥梁。' },
    { year: '2019', title: '品类拓展', desc: '在时尚服饰基础上，新增护肤美妆与生活家居两大核心品类，覆盖全方位品质生活。' },
    { year: '2020', title: '全球直采', desc: '建立覆盖欧洲与美国的专业买手网络，实现源头直采，确保正品与价格优势。' },
    { year: '2022', title: '服务升级', desc: '推出专属顾问一对一服务，打造从选购到售后的全链路品质体验。' },
    { year: '2024', title: '持续创新', desc: '不断探索更多优质品牌与合作模式，以更丰富的好物和更贴心的服务回馈每位顾客。' },
  ];

  const values = [
    { icon: 'ri-award-line', title: '品质至上', desc: '我们相信，真正的价值不在于价格标签，而在于经得起时间考验的品质。每一件入选的商品都经过严格的筛选流程。' },
    { icon: 'ri-heart-line', title: '客户为先', desc: '用心倾听每一位客户的需求，以专业的建议和贴心的服务，让每一次购物都成为愉悦的体验。' },
    { icon: 'ri-lightbulb-line', title: '探索精神', desc: '持续发掘全球范围内兼具品质与设计感的新锐品牌和经典好物，为您打开通往世界精品的大门。' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="关于我们｜欧美精品"
        description="欧美精品汇聚全球顶尖品牌，为您精选来自欧洲与美国的高端正品好物。了解我们的品牌故事、核心价值和联系方式。品质有保障，服务更贴心。"
        keywords="欧美精品品牌,欧美精品介绍,欧美精品联系方式,欧美精品信誉,欧美精品正品保障"
        canonicalUrl="https://www.oumeijingpin.com/about"
        jsonLd={aboutJsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-3">
            关于我们
          </h1>
          <p className="text-sm text-foreground-500 text-center mb-12 max-w-2xl mx-auto">
            欧美精品汇聚全球顶尖品牌，致力于为中国消费者带来纯正的欧美高端品质体验
          </p>

          <div className="max-w-3xl mx-auto mb-16">
            <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20brand%20headquarters%20interior%20with%20warm%20cream%20walls%20and%20natural%20lighting%2C%20minimal%20modern%20design%2C%20high-end%20office%20space%20with%20designer%20furniture%2C%20sophisticated%20corporate%20aesthetic%2C%20soft%20atmosphere&width=1200&height=500&seq=about-hero-01&orientation=landscape"
                alt="欧美精品品牌形象"
                title="欧美精品 - 品牌形象"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">品牌故事</h2>
            <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
              <p>
                欧美精品创立于2018年，总部位于中国上海。我们的创始团队拥有超过十五年的欧美时尚与零售行业经验，深谙国际一线品牌的品质标准与供应链体系。
              </p>
              <p>
                品牌创立的初衷源于一个简单的信念：<strong>每一位追求品质生活的消费者，都应该能够便捷地接触到全球最优质的欧美正品好物。</strong>我们深知在鱼龙混杂的市场中，消费者对正品保障的渴望。因此，从创立第一天起，我们就将"正品"作为品牌的生命线。
              </p>
              <p>
                经过多年的深耕，欧美精品已与超过200个欧洲和美国的知名品牌及授权经销商建立了稳固的合作关系。我们的专业买手团队遍布米兰、巴黎、伦敦、纽约等全球时尚之都，以敏锐的嗅觉和严苛的标准，为您甄选每一件商品。
              </p>
              <p>
                我们不仅是一家跨境电商平台，更是欧美品质生活方式的传递者。通过专业的品牌资讯、科学的选购指南和贴心的顾问服务，我们希望帮助每一位客户找到真正适合自己的好物，让欧美顶级生活方式触手可及。
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 text-center mb-8">发展历程</h2>
            <div className="space-y-0">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-4 md:gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-accent-500 flex-shrink-0 mt-1.5"></div>
                    {i < milestones.length - 1 && <div className="w-px flex-1 bg-accent-200 mt-1"></div>}
                  </div>
                  <div className={`pb-8 ${i === milestones.length - 1 ? 'pb-0' : ''}`}>
                    <span className="text-xs font-semibold text-accent-600">{m.year}</span>
                    <h3 className="font-heading text-base font-medium text-foreground-900 mt-0.5">{m.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 text-center mb-8">核心价值</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {values.map((v, i) => (
                <div key={i} className="text-center p-5 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${v.icon} text-lg text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                <p className="text-xs text-foreground-500">400-888-9999</p>
                <p className="text-[11px] text-foreground-400 mt-0.5">工作日 9:00 - 21:00</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">service@oumeijingpin.com</p>
                <p className="text-[11px] text-foreground-400 mt-0.5">24小时内回复</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">上海市静安区南京西路1688号欧美精品大厦18层</p>
              </div>
            </div>
          </div>

          <div className="max-w-xl mx-auto">
            <div className="bg-background-100 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent-600 font-heading">200+</p>
                  <p className="text-[11px] text-foreground-500 mt-1">合作品牌</p>
                </div>
                <div className="w-px h-10 bg-background-300"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent-600 font-heading">50万+</p>
                  <p className="text-[11px] text-foreground-500 mt-1">服务客户</p>
                </div>
                <div className="w-px h-10 bg-background-300"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent-600 font-heading">99.7%</p>
                  <p className="text-[11px] text-foreground-500 mt-1">好评率</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}